function lexicalToString(lexicalJson: any) {
  // Parse the JSON if it's a string
  const lexicalData =
    typeof lexicalJson === "string" ? JSON.parse(lexicalJson) : lexicalJson;

  // Start with the root node
  const root = lexicalData?.root || lexicalData;

  return processNodeToString(root).trim();
}

function processNodeToString(node: any) {
  if (!node) return "";
  if (!node.children && node.type !== "text") return "";

  let result = "";

  // Process based on node type
  switch (node.type) {
    case "root":
      // Process all children of root and join with double newlines
      result = node.children
        .map((child: any) => processNodeToString(child))
        .filter((content: any) => content.trim()) // Remove empty strings
        .join("\n\n");
      break;

    case "paragraph":
      // Process all children of paragraph and join them
      result = node.children
        .map((child: any) => processNodeToString(child))
        .join("");
      break;

    case "heading":
      // For headings, just get the text content
      result = node.children
        .map((child: any) => processNodeToString(child))
        .join("");
      break;

    case "list":
      // Process list items and join with newlines
      result = node.children
        .map((child: any, index: number) => {
          const itemContent = processNodeToString(child);

          if (node.listType === "number" || node.tag === "ol") {
            // Ordered list
            const start = node.start || 1;
            const itemNumber = start + index;
            return `${itemNumber}. ${itemContent}`;
          } else if (node.listType === "check") {
            // Checkbox list
            const isChecked = child.checked ? "[x]" : "[ ]";
            return `${isChecked} ${itemContent}`;
          } else {
            // Unordered list
            return `• ${itemContent}`;
          }
        })
        .join("\n");
      break;

    case "listitem":
      // Process list item content
      result = node.children
        .map((child: any) => processNodeToString(child))
        .join("");
      break;

    case "quote":
      // For blockquotes, add quote prefix
      const quoteContent = node.children
        .map((child: any) => processNodeToString(child))
        .join("\n");
      result = quoteContent
        .split("\n")
        .map((line: any) => `"${line}"`)
        .join("\n");
      break;

    case "code":
      // For code blocks, just return the code content
      result = node.children
        .map((child: any) => processNodeToString(child))
        .join("\n");
      break;

    case "text":
      // For text nodes, just get the text content (no formatting)
      result = node.text || "";
      break;

    case "link":
      // For link nodes, get the link text and optionally include URL
      const linkText = node.children
        .map((child: any) => processNodeToString(child))
        .join("");
      // You can choose to include URL or not
      result = `${linkText} (${node.url})`;
      // Or just: result = linkText;
      break;

    case "image":
      // For image nodes, use alt text or a placeholder
      result = node.altText || "[Image]";
      break;

    case "horizontalrule":
      // For horizontal rule, use a text separator
      result = "---";
      break;

    case "table":
      // For tables, process rows and separate with newlines
      if (node.children && node.children.length > 0) {
        result = node.children
          .map((row: any) => processNodeToString(row))
          .join("\n");
      }
      break;

    case "tablerow":
      // For table rows, join cells with tabs or spaces
      if (node.children && node.children.length > 0) {
        result = node.children
          .map((cell: any) => processNodeToString(cell))
          .join("\t"); // Use tab separator
      }
      break;

    case "tablecell":
      // For table cells, process content
      result = node.children
        .map((child: any) => processNodeToString(child))
        .join("");
      break;

    default:
      // For unhandled node types, process children if they exist
      if (node.children) {
        result = node.children
          .map((child: any) => processNodeToString(child))
          .join("");
      }
  }

  return result;
}

// Export both functions for use
/**
 * Richtext Payload (Lexical object | chuỗi JSON | chuỗi thường | null) → text.
 * Không bao giờ throw: field richtext có lúc là object, lúc là text thường.
 */
function richTextToPlain(raw: unknown): string {
  if (!raw) return "";
  try {
    return lexicalToString(raw);
  } catch {
    return typeof raw === "string" ? raw : "";
  }
}

export { lexicalToString, processNodeToString, richTextToPlain };
